import { useEffect } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useSearchParams, Link } from 'react-router-dom'
import type { Currency, TransactionResponse, TxStatus, TxType } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { crypto, fiat, shortAddress } from '@/shared/lib/format'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Pagination } from '@/shared/ui/Pagination'
import { Section } from '@/shared/ui/Section'
import { Empty, Panel } from '@/shared/ui/Panel'
import { Select } from '@/shared/ui/Select'
import { Strip } from '@/shared/ui/Strip'
import {
  ArrowRightIcon, DepositIcon, SearchIcon, WithdrawIcon,
} from '@/shared/ui/icons'
import { HistoryStore } from '@/stores/HistoryStore'
import { useCabinet } from '@/stores/RootStore'
import styles from './History.module.css'
import { explain } from '@/shared/lib/errors'
import { useMeta } from '@/shared/lib/hooks/useMeta'

const CURRENCIES: { value: Currency; label: string }[] = [
  { value: 'btc', label: 'BTC (Bitcoin)' },
  { value: 'ltc', label: 'LTC (Litecoin)' },
  { value: 'gram', label: 'GRAM (TON)' },
  { value: 'xmr', label: 'XMR (Monero)' },
  { value: 'usdt_trc20', label: 'USDT (TRC20)' },
  { value: 'usdt_ton', label: 'USDT (TON)' },
]

const TYPES: { value: TxType; label: string }[] = [
  { value: 'receive', label: 'Получение' },
  { value: 'send', label: 'Отправка' },
]

const STATUSES: { value: TxStatus; label: string }[] = [
  { value: 'success', label: 'Успешно' },
  { value: 'processing', label: 'Обработка' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'reversed', label: 'Отменена' },
]

const ALL = '__all__'

/**
 * История операций: фильтры, страницы, детали по клику.
 *
 * Фильтры отдаются серверу — см. HistoryStore. Строка целиком кликабельна,
 * а не только стрелка справа: попасть по стрелке пальцем на телефоне
 * трудно, а операцию открывают, чтобы найти хеш и проверить перевод.
 */
export const HistoryPage = observer(function HistoryPage() {
  useMeta('История')
  const store = useLocalObservable(() => new HistoryStore())
  const cabinet = useCabinet()
  const [params] = useSearchParams()

  useEffect(() => {
    // Переход из обзора «История» по конкретной валюте.
    const currency = params.get('currency') as Currency | null
    if (currency) store.setCurrency(currency)
    else store.load()
  }, [store, params])

  const page = store.page.value
  const items = page?.items ?? []

  return (
    <Section title="История">
      <Panel>
        <div className={styles.filters}>
          <div className={styles.selects}>
              <Select
                label="Валюта" labelFrom={768} placeholder="Все валюты"
                options={[{ value: ALL, label: 'Все валюты' },
                          ...CURRENCIES.map((c) => ({ value: c.value, label: c.label }))]}
                value={store.currency ?? ALL}
                onChange={(v) => store.setCurrency(v === ALL ? null : (v as Currency))}
              />
              <Select
                label="Тип операции" labelFrom={768} placeholder="Все операции"
                options={[{ value: ALL, label: 'Все операции' },
                          ...TYPES.map((t) => ({ value: t.value, label: t.label }))]}
                value={store.type ?? ALL}
                onChange={(v) => store.setType(v === ALL ? null : (v as TxType))}
              />
              <Select
                label="Статус" labelFrom={768} placeholder="Все статусы"
                options={[{ value: ALL, label: 'Все статусы' },
                          ...STATUSES.map((s) => ({ value: s.value, label: s.label }))]}
                value={store.status ?? ALL}
                onChange={(v) => store.setStatus(v === ALL ? null : (v as TxStatus))}
              />
          </div>
          <div className={styles.dates}>
            <Field label="Период" labelFrom={1280}
                   type="text" placeholder="Дата от"
                   value={store.dateFrom}
                   onChange={(e) => store.setDateFrom(e.target.value)}
                   onFocus={(e) => { e.target.type = 'date' }}
                   onBlur={(e) => { if (!e.target.value) e.target.type = 'text' }} />
            <span className={styles.dash} aria-hidden="true">-</span>
            <Field aria-label="Дата до"
                   type="text" placeholder="Дата до"
                   value={store.dateTo}
                   onChange={(e) => store.setDateTo(e.target.value)}
                   onFocus={(e) => { e.target.type = 'date' }}
                   onBlur={(e) => { if (!e.target.value) e.target.type = 'text' }} />
          </div>

          <div className={styles.search}>
            <Field placeholder="Адрес или TxID" icon={<SearchIcon size={24} />}
                   aria-label="Поиск по адресу или TxID"
                   value={store.search} onChange={(e) => store.setSearch(e.target.value)} />
          </div>
        </div>

        {store.page.error && !page ? (
          <Strip tone="bad">{explain(store.page.error)}</Strip>
        ) : null}

        {store.page.initialLoading ? (
          <Empty title="Загружаем операции…" />
        ) : items.length === 0 ? (
          <Empty
            title="Операций пока нет"
            {...(store.isFiltered
              ? {}
              : { note: 'Здесь появятся получения, отправки и автовыводы после первых операций.' })}
            action={store.isFiltered
              ? <Button variant="secondary" onClick={() => store.reset()}>Сбросить фильтры</Button>
              : undefined}
          />
        ) : (
          <>
            <div className={styles.table}>
              <div className={styles.headRow} role="row">
                <span className={`${styles.cell} ${styles.headCell} ${styles.mid}`}>Дата</span>
                <span className={`${styles.cell} ${styles.headCell} ${styles.mid}`}>Операция</span>
                <span className={`${styles.cell} ${styles.headCell} ${styles.wide}`}>Валюта</span>
                <span className={`${styles.cell} ${styles.headCell}`}>Сумма</span>
                <span className={`${styles.cell} ${styles.headCell} ${styles.wide}`}>Адрес / TxID</span>
                <span className={`${styles.cell} ${styles.headCell}`}>Статус</span>
                <span className={`${styles.cell} ${styles.headCell}`}>Ещё</span>
              </div>
              {items.map((tx) => (
                <Row key={tx.id} tx={tx} view={cabinet.view}
                     onOpen={() => void store.openDetails(tx.id)} />
              ))}
            </div>
            <Pagination
              total={page?.total ?? 0}
              limit={store.limit}
              offset={store.offset}
              onChange={(offset) => store.setOffset(offset)}
            />
          </>
        )}
      </Panel>

      {store.details ? (
        <Details store={store} view={cabinet.view} />
      ) : null}
    </Section>
  )
})

function Row({ tx, view, onOpen }: {
  tx: TransactionResponse; view: 'usd' | 'rub'; onOpen: () => void
}) {
  const coin = coinOf(tx.currency)
  const incoming = tx.type === 'receive'
  return (
    <button type="button" className={styles.row} onClick={onOpen}>
      <span className={`${styles.cell} ${styles.date} ${styles.mid}`}>
        {dateTime(tx.created_at)}
      </span>
      <span className={`${styles.cell} ${styles.mid}`}>
        {incoming ? 'Получение' : 'Отправка'}
      </span>
      <span className={`${styles.cell} ${styles.wide}`}>
        {coin.ticker} ({networkOf(tx.currency)})
      </span>
      <span className={`${styles.cell} ${styles.amount}`}>
        <span className={styles.amountTop}>
          <span className={styles.amountValue}>{crypto(tx.amount)} {coin.ticker}</span>
          <span className={styles.way}>
            {incoming ? <DepositIcon size={16} /> : <WithdrawIcon size={16} />}
          </span>
        </span>
        <span className={styles.amountFiat}>{fiat(tx.amount_fiat, view)}</span>
      </span>
      <span className={`${styles.cell} ${styles.addr} ${styles.wide}`}>
        <span className={styles.mono}>{tx.address ? shortAddress(tx.address, 8, 4) : '—'}</span>
        <span className={styles.monoSoft}>{tx.txid ? shortAddress(tx.txid, 8, 4) : '—'}</span>
      </span>
      <Status status={tx.status} />
      <span className={`${styles.cell} ${styles.center}`}>
        <span className={styles.go}><ArrowRightIcon size={24} /></span>
      </span>
    </button>
  )
}

function Status({ status }: { status: TxStatus }) {
  const map: Record<TxStatus, { text: string; tone: string }> = {
    success: { text: 'Успешно', tone: styles.ok ?? '' },
    processing: { text: 'Обработка', tone: styles.wait ?? '' },
    failed: { text: 'Ошибка', tone: styles.bad ?? '' },
    reversed: { text: 'Отменена', tone: styles.soft ?? '' },
  }
  const item = map[status]
  return (
    <span className={`${styles.cell} ${styles.centerNarrow}`}>
      <span className={`${styles.state} ${item.tone}`}>
        <span className={styles.dot} />
        {/* На узком экране от статуса остаётся одна точка, но слово
            обязано дойти до читалки: цвет — не текст. Поэтому оно
            скрыто визуально, а не удалено из разметки. */}
        <span className={styles.stateText}>{item.text}</span>
      </span>
    </span>
  )
}

const Details = observer(function Details({ store, view }: {
  store: HistoryStore; view: 'usd' | 'rub'
}) {
  const tx = store.details
  if (!tx) return null
  const coin = coinOf(tx.currency)

  // Ссылка на апелляцию с уже подставленными реквизитами: перепечатывать
  // UUID и hash руками — верный способ ошибиться в одном символе
  // и получить от поддержки «операция не найдена».
  const appeal = `/app/appeal?tx=${encodeURIComponent(tx.id)}`
    + (tx.txid ? `&txid=${encodeURIComponent(tx.txid)}` : '')

  return (
    <Modal title="Детали операции" onClose={() => store.closeDetails()}
           actions={
             <>
               <Link className={styles.appeal} to={appeal}
                     onClick={() => store.closeDetails()}>
                 Проблема с операцией
               </Link>
               <Button onClick={() => store.closeDetails()}>Закрыть</Button>
             </>
           }>
      <div className={styles.detailRows}>
        <DetailRow label="Дата" value={dateTime(tx.created_at)} />
        <DetailRow label="Тип операции" value={tx.type === 'receive' ? 'Получение' : 'Отправка'} />
        <DetailRow label="Валюта" value={`${coin.ticker} (${networkOf(tx.currency)})`} />
        {/* Сумма — полной точностью: по ней сверяют перевод в обозревателе. */}
        <DetailRow label="Сумма" value={`${tx.amount} ${coin.ticker}`} />
        <DetailRow label="В фиате" value={fiat(tx.amount_fiat, view)} />
        {tx.network_fee ? (
          <DetailRow label="Комиссия сети" value={`${tx.network_fee} ${coin.ticker}`} />
        ) : null}
        <DetailRow label="Адрес получателя" value={tx.address || '—'} />
        <DetailRow label="TxID" value={tx.txid || '—'} />
        <DetailRow label="Номер операции" value={tx.id} />
      </div>
    </Modal>
  )
})

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  )
}

/** «19.06.2026 / 15:20» — как в макете. */
function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
    + ` / ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
